import { Link } from "@tanstack/react-router";
import { useParams } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { resourceCategoryRoute } from "app/router";
import { Card, CardTitle, Input, Spinner, TablePagination } from "components/elements";
import { BlogPreview } from "components/elements/BlogPreview";
import { FavoriteArticlesSidebar } from "components/layout/FavoriteArticlesSidebar";
import { RowMasonry } from "components/layout/RowMasonry";
import { TrendingRow } from "components/layout/TrendingRow";
import { TrendingSidebar } from "components/layout/TrendingSidebar";
import { trpc } from "lib/trpc";
import { ResourcesPostPreview } from "@compass/shared/types";

export const ResourceCategory = () => {
    const { slug } = useParams({ from: resourceCategoryRoute.id });

    useEffect(() => {
        const scrollable = document.getElementById("scrollable-content");
        scrollable?.scrollTo({ top: 0, behavior: "smooth" });
    }, [slug]);

    const [search, setSearch] = useState("");
    const [pageSize, setPageSize] = useState<number | undefined>(10);
    const [currentPage, setCurrentPage] = useState<number | undefined>(0);

    const [category] = trpc.resources.getCategory.useSuspenseQuery({ slug });
    const postsQuery = trpc.resources.latestPosts.useQuery({
        category: slug,
        search: search.length ? search : undefined,
        page: currentPage ? currentPage + 1 : 1,
        limit: pageSize || 10, // Use selected page size or default to 10
    });

    if ("error" in category || "error" in (postsQuery.data ?? {}) || postsQuery.error) {
        return (
            <div className="flex h-full flex-col items-center justify-center">
                <h1 className="text-4xl font-bold text-slate-500">404</h1>
                <p className="flex text-2xl">Category not found</p>
                <Link to={`/c/blog` as "/social"} className="text-sm underline">
                    Back
                </Link>
            </div>
        );
    }

    const posts = postsQuery.data;

    if (posts && "error" in posts) {
        throw new Error();
    }

    const onChangePage = (page: number | undefined) => {
        setCurrentPage(page);
    };

    const onChangePageSize = (pageSize: number | undefined) => {
        setPageSize(pageSize);
    };

    const categoryLabel = category.name;

    return (
        <Card>
            <TrendingRow filterType="blogPost" title={`Trending ${categoryLabel}`} limit={5} />
            <div className="flex w-full items-center justify-between gap-4">
                <div className="font-sans text-[28px] font-semibold text-[#424242]">
                    {categoryLabel}
                </div>

                <div className="flex max-w-md flex-1 justify-end">
                    <div className="w-full">
                        <Input
                            type="text"
                            placeholder="Search"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="bg-brand-polar w-full border border-[#d4d5d5]"
                            style={{ outline: "none", boxShadow: "none" }}
                        />
                    </div>
                </div>
            </div>

            <p className="mb-4">{category.description}</p>
            {/* Layout: main content + right sidebar */}
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
                <div className="lg:col-span-2">
                    {postsQuery.isPending && (
                        <div className="flex h-60 items-center justify-center">
                            <Spinner className="text-slate-500" />
                        </div>
                    )}

                    {!postsQuery.isPending && !posts?.posts?.length && (
                        <p className="text-center">No posts could be found</p>
                    )}

                    {Boolean(posts?.posts?.length && search && postsQuery.isPending) && (
                        <p className="text-center">
                            Showing {posts?.posts?.length} posts for search
                        </p>
                    )}

                    {Boolean(posts?.posts?.length) && (
                        <RowMasonry
                            items={posts?.posts ?? []}
                            getKey={(post) => post.id}
                            columns={{ base: 1, md: 2, lg: 3 }}
                            renderItem={(post: ResourcesPostPreview) => (
                                <BlogPreview post={post} category={category.slug} />
                            )}
                        />
                    )}

                    {(posts?.posts?.length ?? 0) > 0 && (
                        <div className="mt-6">
                            <TablePagination
                                total={posts?.totalCount || 1}
                                page={currentPage}
                                onChangePage={onChangePage}
                                pageSize={pageSize}
                                onChangePageSize={onChangePageSize}
                                message="Items per page"
                            />
                        </div>
                    )}
                </div>

                {/* Right sidebar */}
                <aside className="hidden lg:block">
                    <TrendingSidebar
                        filterType="blogPost"
                        title={`Trending ${categoryLabel}`}
                        limit={20}
                    />
                    <div className="mt-4" />
                    <FavoriteArticlesSidebar />
                </aside>
            </div>
        </Card>
    );
};
